import { NavLink } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Bot,
  CreditCard,
  Cpu,
  FileSearch,
  Instagram,
  LayoutDashboard,
  MessageCircle,
  Server,
  Settings2,
  Users,
  Wallet,
  Layers,
  UserCog,
  Calculator,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { copy } from '../lib/copy';
import { useOperatorPermissions } from '../hooks/useOperatorPermissions';
import { cn } from '../lib/utils';
import { ADMIN_PRODUCT, type AdminProductVariant } from '../lib/admin-product';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  visible: boolean;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const scopedPath = (scope: 'app' | 'studio' | 'platform', subpath = '') =>
  `/${scope}${subpath ? `/${subpath}` : ''}`;

export function Sidebar({
  product = ADMIN_PRODUCT,
  mobileOpen,
  onNavigate,
}: {
  product?: AdminProductVariant;
  mobileOpen?: boolean;
  onNavigate?: () => void;
}) {
  const perms = useOperatorPermissions();

  const sections: NavSection[] = [
    {
      title: 'APP',
      items: [
        { to: scopedPath('app'), label: copy.nav.dashboard, icon: LayoutDashboard, end: true, visible: true },
        { to: scopedPath('app', 'users'), label: copy.nav.users, icon: Users, visible: true },
        { to: scopedPath('app', 'agents'), label: copy.nav.agents, icon: Bot, visible: true },
        { to: scopedPath('app', 'plans'), label: 'APP тарифы', icon: Wallet, visible: true },
        { to: scopedPath('app', 'economics'), label: 'Экономика', icon: Calculator, visible: perms.canViewBilling },
        { to: scopedPath('app', 'models'), label: copy.nav.modelsApp, icon: Cpu, visible: perms.canViewModels },
        {
          to: scopedPath('app', 'usage'),
          label: copy.nav.usage,
          icon: BarChart3,
          visible: perms.canViewUsage,
        },
        {
          to: scopedPath('app', 'instagram'),
          label: copy.nav.instagram,
          icon: Instagram,
          visible: perms.canViewInstagram,
        },
        {
          to: scopedPath('app', 'telegram-business'),
          label: copy.nav.telegram,
          icon: MessageCircle,
          visible: perms.canViewTelegramBusiness,
        },
      ],
    },
    {
      title: 'STUDIO',
      items: [
        { to: scopedPath('studio'), label: copy.nav.dashboard, icon: LayoutDashboard, end: true, visible: true },
        { to: scopedPath('studio', 'workspaces'), label: 'Workspaces', icon: Layers, visible: perms.canViewWorkspaces },
        { to: scopedPath('studio', 'users'), label: copy.nav.users, icon: Users, visible: true },
        { to: scopedPath('studio', 'plans'), label: 'Studio тарифы', icon: Wallet, visible: true },
        { to: scopedPath('studio', 'economics'), label: 'Экономика', icon: Calculator, visible: perms.canViewBilling },
        {
          to: scopedPath('studio', 'models'),
          label: copy.nav.modelsStudio,
          icon: Cpu,
          visible: perms.canViewModels,
        },
        {
          to: scopedPath('studio', 'usage'),
          label: copy.nav.usage,
          icon: BarChart3,
          visible: perms.canViewUsage,
        },
      ],
    },
    {
      title: 'PLATFORM',
      items: [
        {
          to: scopedPath('platform'),
          label: copy.nav.dashboard,
          icon: LayoutDashboard,
          end: true,
          visible: true,
        },
        { to: scopedPath('platform', 'users'), label: 'Идентичности', icon: Users, visible: true },
        {
          to: scopedPath('platform', 'workspaces'),
          label: copy.nav.workspaces,
          icon: Layers,
          visible: perms.canViewWorkspaces,
        },
        {
          to: scopedPath('platform', 'billing'),
          label: copy.nav.billing,
          icon: CreditCard,
          visible: perms.canViewBilling,
        },
        { to: scopedPath('platform', 'platform'), label: copy.nav.platform, icon: Settings2, visible: true },
        { to: scopedPath('platform', 'system'), label: copy.nav.system, icon: Server, visible: true },
        {
          to: scopedPath('platform', 'audit'),
          label: copy.nav.audit,
          icon: FileSearch,
          visible: perms.canViewOperatorAudit || perms.canViewSecurityAudit,
        },
        {
          to: scopedPath('platform', 'team'),
          label: copy.nav.team,
          icon: UserCog,
          visible: perms.canManageTeam,
        },
      ],
    },
  ];

  return (
    <aside
      className={cn(
        'flex w-[260px] shrink-0 flex-col border-r border-ops-border bg-ops-surface',
        'fixed inset-y-0 left-0 z-40 transition-transform lg:static lg:z-auto',
        mobileOpen ? 'translate-x-0 shadow-card' : '-translate-x-full lg:translate-x-0',
      )}
    >
      <div className="px-4 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[18px] bg-ops-accent-soft">
            <Activity className="h-5 w-5 text-ops-text" />
          </div>
          <div>
            <p className="text-sm font-semibold text-ops-text">{copy.brand.title}</p>
            <p className="text-xs text-ops-muted">{copy.brand.subtitle}</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
        {sections
          .filter(
            (section) => product === 'all' || section.title.toLowerCase() === product,
          )
          .map((section) => {
          const visibleItems = section.items.filter((item) => item.visible);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title}>
              <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-ops-muted">
                {section.title}
              </p>
              <div className="space-y-1">
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn('ops-nav-item', isActive && 'ops-nav-item--active')
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
